// Phase 4 (grade-2 ceiling) — `clock` concept visual for telling time.
//
// Renders an analog clock FACE (SVG): a circle, 12 hour ticks, an hour hand
// and a minute hand positioned from the visual's fields. ADDITIVE — registers
// itself under the "clock" kind (see rendererRegistry); it plugs in with NO
// edit to MathVisual or the session screens (the registry resolves it by key),
// exactly like array / place_value_blocks / fraction_bar. No skill targets it
// yet, so it stays invisible to kids until content opts in.
//
// FIELD-CONVENTION CONTRACT (read these off the shared MathRendererProps —
// the future mapper branch in contentToEngineItem.ts MUST emit them this way):
//   • hours   <- `a`      (0–23; wraps to a 12-hour face — 13 -> 1 o'clock)
//   • minutes <- `b`      (0–59)
//   • total   <- total minutes since midnight (a*60 + b), for contract parity
//              with the other visuals. The renderer reads `a`/`b` directly,
//              but if BOTH are absent it FALLS BACK to deriving hours/minutes
//              from `total` (floor(total/60) : total%60) so a payload that
//              only carries `total` still renders a meaningful time.
// FAIL-SAFE: missing/non-finite fields degrade to 12:00 (an "empty" clock —
// both hands straight up) rather than rendering nothing or NaN coordinates.
// The hour hand advances smoothly with the minutes (e.g. 3:30 -> hour hand
// halfway between 3 and 4), mirroring a real analog clock.
import type { MathRendererProps } from "./rendererRegistry";

const FACE = "var(--color-bg-secondary, #f5efe3)";
const RIM = "var(--color-border-secondary, #cdbfae)";
const TICK = "var(--color-text-secondary, #7a6f60)";
const HOUR_HAND = "var(--pal-orange, #d97742)";
const MIN_HAND = "var(--pal-green, #5a9e6f)";

const SIZE = 220; // viewBox edge (square)
const C = SIZE / 2; // center
const R = C - 8; // face radius (leave room for the rim stroke)

/** Polar -> cartesian on the clock face. `frac` is 0..1 around the dial
 *  (0 = 12 o'clock, increasing clockwise); `len` is distance from center. */
function point(frac: number, len: number): { x: number; y: number } {
  const ang = frac * 2 * Math.PI - Math.PI / 2; // -90deg so 0 is straight up
  return { x: C + len * Math.cos(ang), y: C + len * Math.sin(ang) };
}

function ClockRenderer({ a, b, total }: MathRendererProps) {
  // Resolve hours/minutes from a/b; fall back to deriving them from `total`
  // (minutes-since-midnight) when BOTH structured fields are absent.
  const haveAB = a !== undefined || b !== undefined;
  const rawHours = haveAB ? a : Math.floor((total ?? 0) / 60);
  const rawMins = haveAB ? b : (total ?? 0) % 60;

  // Clamp/normalize fail-safe: non-finite -> 0, then wrap into range. Hours
  // fold onto a 12-hour face (0/12 -> 12 o'clock position, i.e. frac 0).
  const safe = (v: number | undefined, mod: number) => {
    const n = Number.isFinite(v as number) ? Math.floor(v as number) : 0;
    return ((n % mod) + mod) % mod;
  };
  const hours = safe(rawHours, 12); // 0..11 (12-hour face)
  const minutes = safe(rawMins, 60); // 0..59

  // Fractions around the dial. The hour hand advances with the minutes so a
  // half-past time sits the hour hand mid-way between the two hour marks.
  const minFrac = minutes / 60;
  const hourFrac = (hours + minutes / 60) / 12;

  const hourTip = point(hourFrac, R * 0.5);
  const minTip = point(minFrac, R * 0.78);

  const displayH = hours === 0 ? 12 : hours;
  const mm = String(minutes).padStart(2, "0");

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      width="100%"
      style={{ maxWidth: 220 }}
      role="img"
      aria-label={`Clock showing ${displayH}:${mm}`}
    >
      <circle cx={C} cy={C} r={R} fill={FACE} stroke={RIM} strokeWidth="4" />
      {/* 12 hour ticks */}
      {Array.from({ length: 12 }).map((_, i) => {
        const outer = point(i / 12, R - 4);
        const inner = point(i / 12, R - (i % 3 === 0 ? 18 : 12));
        return (
          <line
            key={i}
            x1={inner.x}
            y1={inner.y}
            x2={outer.x}
            y2={outer.y}
            stroke={TICK}
            strokeWidth={i % 3 === 0 ? 4 : 2}
            strokeLinecap="round"
          />
        );
      })}
      {/* minute hand (longer, thinner) */}
      <line
        x1={C}
        y1={C}
        x2={minTip.x}
        y2={minTip.y}
        stroke={MIN_HAND}
        strokeWidth="4"
        strokeLinecap="round"
      />
      {/* hour hand (shorter, thicker) */}
      <line
        x1={C}
        y1={C}
        x2={hourTip.x}
        y2={hourTip.y}
        stroke={HOUR_HAND}
        strokeWidth="6"
        strokeLinecap="round"
      />
      {/* center pin */}
      <circle cx={C} cy={C} r="6" fill={HOUR_HAND} />
    </svg>
  );
}

export default ClockRenderer;
